import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Icon } from '@/shared/ui/icon';
import { Button } from './Button';
import { cn } from '@/shared/lib/cn';
import { useT } from '@/shared/i18n';
import {
  ToastContext,
  type ToastItem,
  type ToastOptions,
  type ToastTone,
} from './toast-context';

const iconByTone: Record<ToastTone, ReactNode> = {
  success: <Icon name="check-circle" size={18} />,
  error: <Icon name="alert-circle" size={18} />,
  info: <Icon name="info" size={18} />,
  warning: <Icon name="triangle-alert" size={18} />,
};

const toneClasses: Record<ToastTone, string> = {
  success:
    'border-[var(--success-border)] bg-[var(--surface-raised)] text-[var(--success-text)]',
  error:
    'border-[var(--danger-border)] bg-[var(--surface-raised)] text-[var(--danger-text)]',
  info:
    'border-[var(--border-strong)] bg-[var(--surface-raised)] text-[var(--text-primary)]',
  warning:
    'border-[var(--warning-border)] bg-[var(--surface-raised)] text-[var(--warning-text)]',
};

function createToastId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `toast-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const notify = useCallback((toast: ToastOptions) => {
    const id = createToastId();
    setToasts((current) => [
      ...current,
      {
        ...toast,
        id,
        tone: toast.tone ?? 'info',
        durationMs: toast.durationMs ?? 5000,
      },
    ]);
    return id;
  }, []);

  const value = useMemo(() => ({ notify, dismiss }), [notify, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}): ReactNode {
  return (
    <div
      aria-live="polite"
      aria-relevant="additions removals"
      className="pointer-events-none fixed end-4 top-4 z-50 flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-3"
    >
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastCard({
  toast,
  onDismiss,
}: {
  toast: ToastItem;
  onDismiss: (id: string) => void;
}): ReactNode {
  const { t } = useT();

  useEffect(() => {
    if (toast.durationMs === 0) {
      return;
    }
    const timeout = window.setTimeout(() => onDismiss(toast.id), toast.durationMs);
    return () => window.clearTimeout(timeout);
  }, [onDismiss, toast.durationMs, toast.id]);

  return (
    <section
      role={toast.tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'pointer-events-auto flex gap-3 rounded-lg border p-4 shadow-panel',
        toneClasses[toast.tone],
      )}
    >
      <span className="mt-0.5 shrink-0">{iconByTone[toast.tone]}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-[var(--text-primary)]">
          {toast.title}
        </p>
        {toast.description ? (
          <p className="mt-1 text-sm leading-relaxed text-[var(--text-secondary)]">
            {toast.description}
          </p>
        ) : null}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={t('toast.dismiss')}
        className="h-7 w-7 shrink-0 p-0"
        onClick={() => onDismiss(toast.id)}
      >
        <Icon name="close" size={14} />
      </Button>
    </section>
  );
}
